/**
 * ReportsPage — pipeline velocity, forecast, activity trends, lost deal
 * analysis. Tabbed view with one table per report.
 */

import { useMemo, useState } from 'react';
import {
  useFiasLeadsPipelineVelocity,
  useFiasLeadsForecast,
  useFiasLeadsActivityTrends,
  useFiasLeadsLostDeals,
  useFiasLeadsPipelines,
} from '../hooks/useFiasLeads';
import { SkeletonBlock } from '../components/Skeletons';

interface Props {
  holderId: string;
}

type Tab = 'velocity' | 'forecast' | 'activity-trends' | 'lost-deals';

export function ReportsPage({ holderId }: Props) {
  const [tab, setTab] = useState<Tab>('forecast');
  const [pipelineId, setPipelineId] = useState<string>('');
  const pipelinesQuery = useFiasLeadsPipelines(holderId);

  return (
    <div className="p-6 max-w-6xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Reports</h1>
          <p className="text-sm text-muted-foreground">Pipeline analytics.</p>
        </div>
        <select
          value={pipelineId}
          onChange={(e) => setPipelineId(e.target.value)}
          className="rounded border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="">All pipelines</option>
          {(pipelinesQuery.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </header>

      <div className="flex gap-1 rounded border border-border bg-card p-1 w-fit">
        <TabBtn label="Forecast" active={tab === 'forecast'} onClick={() => setTab('forecast')} />
        <TabBtn
          label="Pipeline velocity"
          active={tab === 'velocity'}
          onClick={() => setTab('velocity')}
        />
        <TabBtn
          label="Activity trends"
          active={tab === 'activity-trends'}
          onClick={() => setTab('activity-trends')}
        />
        <TabBtn
          label="Lost deals"
          active={tab === 'lost-deals'}
          onClick={() => setTab('lost-deals')}
        />
      </div>

      {tab === 'forecast' && <ForecastReport holderId={holderId} pipelineId={pipelineId} />}
      {tab === 'velocity' && <VelocityReport holderId={holderId} pipelineId={pipelineId} />}
      {tab === 'activity-trends' && <ActivityTrendsReport holderId={holderId} />}
      {tab === 'lost-deals' && <LostDealsReport holderId={holderId} pipelineId={pipelineId} />}
    </div>
  );
}

function TabBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'rounded px-3 py-1 text-sm transition-colors ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:text-foreground')
      }
    >
      {label}
    </button>
  );
}

function ForecastReport({ holderId, pipelineId }: { holderId: string; pipelineId: string }) {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const q = useFiasLeadsForecast(holderId, pipelineId || undefined);
  const rows = useMemo(() => {
    const data = q.data ?? [];
    return [...data].sort((a, b) => {
      const cmp = a.month.localeCompare(b.month);
      return order === 'asc' ? cmp : -cmp;
    });
  }, [q.data, order]);
  if (q.isLoading) return <SkeletonBlock lines={4} />;
  return (
    <div className="rounded border border-border bg-card p-4">
      <div className="flex items-center justify-end mb-3">
        <select
          value={order}
          onChange={(e) => setOrder(e.target.value as 'asc' | 'desc')}
          className="rounded border border-border bg-background px-2 py-1 text-xs"
        >
          <option value="asc">Soonest first</option>
          <option value="desc">Latest first</option>
        </select>
      </div>
      <table className="w-full text-sm">
        <thead className="border-b border-border">
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-2">Month</th>
            <th className="py-2">Deals</th>
            <th className="py-2">Total value</th>
            <th className="py-2">Weighted (× win-likelihood)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.month}>
              <td className="py-2">
                {new Date(r.month).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                })}
              </td>
              <td className="py-2">{r.deal_count}</td>
              <td className="py-2">${Number(r.total_value).toLocaleString()}</td>
              <td className="py-2">${Number(r.weighted_value).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="p-6 text-center text-muted-foreground text-sm">
          No open deals with expected close dates.
        </p>
      )}
    </div>
  );
}

function VelocityReport({ holderId, pipelineId }: { holderId: string; pipelineId: string }) {
  const q = useFiasLeadsPipelineVelocity(holderId, pipelineId || undefined);
  if (q.isLoading) return <SkeletonBlock lines={4} />;
  const rows = q.data ?? [];
  return (
    <div className="rounded border border-border bg-card p-4">
      <table className="w-full text-sm">
        <thead className="border-b border-border">
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-2">Stage</th>
            <th className="py-2">Deals</th>
            <th className="py-2">Avg days in stage</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.stage_id}>
              <td className="py-2">{r.stage_name}</td>
              <td className="py-2">{r.deal_count}</td>
              <td className="py-2">{Number(r.avg_days_in_stage).toFixed(1)} days</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="p-6 text-center text-muted-foreground text-sm">No deals yet.</p>
      )}
    </div>
  );
}

function ActivityTrendsReport({ holderId }: { holderId: string }) {
  const [days, setDays] = useState(30);
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const q = useFiasLeadsActivityTrends(holderId, days);
  const rows = useMemo(() => {
    const data = q.data ?? [];
    return [...data].sort((a, b) => {
      const dayCmp = a.day.localeCompare(b.day);
      const primary = order === 'desc' ? -dayCmp : dayCmp;
      return primary !== 0 ? primary : a.kind.localeCompare(b.kind);
    });
  }, [q.data, order]);
  if (q.isLoading) return <SkeletonBlock lines={4} />;
  return (
    <div className="rounded border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-muted-foreground">Activity by day + kind</h2>
        <div className="flex gap-2">
          <select
            value={order}
            onChange={(e) => setOrder(e.target.value as 'desc' | 'asc')}
            className="rounded border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="desc">Recent first</option>
            <option value="asc">Oldest first</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded border border-border bg-background px-2 py-1 text-xs"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="border-b border-border">
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-2">Day</th>
            <th className="py-2">Kind</th>
            <th className="py-2">Count</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={`${r.day}-${r.kind}`}>
              <td className="py-2">{r.day}</td>
              <td className="py-2 uppercase text-xs">{r.kind}</td>
              <td className="py-2">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="p-6 text-center text-muted-foreground text-sm">No activity recorded.</p>
      )}
    </div>
  );
}

function LostDealsReport({ holderId, pipelineId }: { holderId: string; pipelineId: string }) {
  const q = useFiasLeadsLostDeals(holderId, pipelineId || undefined);
  if (q.isLoading) return <SkeletonBlock lines={4} />;
  const rows = q.data ?? [];
  return (
    <div className="rounded border border-border bg-card p-4">
      <table className="w-full text-sm">
        <thead className="border-b border-border">
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-2">Lost reason</th>
            <th className="py-2">Deals</th>
            <th className="py-2">Total value</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.lost_reason}>
              <td className="py-2">{r.lost_reason}</td>
              <td className="py-2">{r.deal_count}</td>
              <td className="py-2">${Number(r.total_value).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="p-6 text-center text-muted-foreground text-sm">No lost deals.</p>
      )}
    </div>
  );
}
